import React from "react";
import arrow_left from "../assets/images/navbar/arrow-left.png";
import cancel from "../assets/images/navbar/cancel.png";
import { authService, uploadService, UserProfile } from "../services";
import { useProfile } from "../context/ProfileContext";

type EditProfileProps = {
  setIsEdit: (open: boolean) => void;
  userProfile: UserProfile;
  onProfileUpdate: () => void;
};

const EditProfile = ({ setIsEdit, userProfile, onProfileUpdate }: EditProfileProps) => {
  const [firstName, setFirstName] = React.useState(userProfile.firstName || "");
  const [lastName, setLastName] = React.useState(userProfile.lastName || "");
  const [email, setEmail] = React.useState(userProfile.email || "");
  const [imageUrl, setImageUrl] = React.useState(userProfile.image || "");
  const [selectedCategories, setSelectedCategories] = React.useState<string[]>(
    userProfile.preferences || [],
  );
  const [isLoading, setIsLoading] = React.useState(false);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState("");
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const profileContext = useProfile();

  const categories: string[] = [
    "vegetarian",
    "non-vegetarian", 
    "spicy",
    "mild",
    "pizza",
    "burger",
    "indian",
    "chinese",
    "thai",
    "italian",
    "mexican",
    "fast-food",
    "biryani",
    "dosa",
    "kebab",
    "breakfast",
    "lunch",
    "dinner",
  ];

  const toggleCategory = (category: string) => {
    setSelectedCategories((prev) =>
      prev.includes(category)
        ? prev.filter((c) => c !== category)
        : [...prev, category],
    );
  };

  const formatCategoryDisplay = (category: string) => {
    return category
      .split('-')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setError("Please select a valid image file");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError("Image must be under 5MB");
      return;
    }

    setUploading(true);
    setError("");
    const localPreview = URL.createObjectURL(file);
    setImageUrl(localPreview);

    try {
      const res: any = await uploadService.uploadProfileImage(file);
      const link =
        res?.data?.link ||
        res?.data?.url ||
        res?.link ||
        res?.url ||
        res?.data?.image;
      if (link && !String(link).startsWith("blob:")) {
        URL.revokeObjectURL(localPreview);
        setImageUrl(link);
        setError("");
      } else {
        setError(
          res?.message ||
            "Upload failed. Please try another photo before saving."
        );
        setImageUrl(userProfile.image || "");
        URL.revokeObjectURL(localPreview);
      }
    } catch (err: any) {
      setError(err?.message || "Failed to upload image");
      setImageUrl(userProfile.image || "");
      URL.revokeObjectURL(localPreview);
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSaveChanges = async () => {
    if (!firstName.trim()) {
      setError("First name is required");
      return;
    }

    if (imageUrl.startsWith("blob:")) {
      setError("Please wait for the photo to finish uploading");
      return;
    }

    setIsLoading(true);
    setError("");

    try {
      const updateData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        image: imageUrl || "",
        preferences: selectedCategories,
      };

      const response: any = await authService.updateProfile(updateData);
      
      if (response.status) {
        await onProfileUpdate();
        await profileContext.fetchProfile();
        setIsEdit(false);
      } else {
        setError(response?.message || "Failed to update profile");
      }
    } catch (err: any) {
      console.error("Error updating profile:", err);
      setError(err?.message || err.response?.data?.message || "Failed to update profile. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 z-[60] transition-opacity duration-300"
      onClick={() => setIsEdit(false)}
    >
      <div
        className="fixed right-0 top-0 h-full w-full max-w-[450px] bg-white dark:bg-gray-800 shadow-2xl transform transition-transform duration-300 ease-in-out rounded-l-[20px] shadow-[-23px_0_72px_0_rgba(0,0,0,0.15)] overflow-y-auto"
        style={{ animation: "slideInRight 0.3s ease-out" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="w-full inline-flex flex-col items-start rounded-l-[24px] bg-white dark:bg-gray-800 shadow-[-23px_0_72px_0_rgba(0,0,0,0.15)] p-6 pb-8 transition-colors duration-300">
          <div className="w-full">
            {/* Modal Header */}
            <div className="w-full flex justify-between items-center mb-[24px]">
              <img
                src={arrow_left}
                alt="close"
                className="flex w-[36px] h-[36px] justify-center items-center gap-[7.5px] shrink-0 rounded-full border-[0.75px] border-[rgba(112,107,102,0.25)] bg-[rgba(255,255,255,0.25)] cursor-pointer"
                onClick={() => setIsEdit(false)}
              />
              <img
                src={cancel}
                alt="close"
                className="flex w-[36px] h-[36px] p-[7.5px] justify-center items-center gap-[7.5px] shrink-0 rounded-full border-[0.75px] border-[rgba(112,107,102,0.25)] bg-[rgba(255,255,255,0.25)] cursor-pointer"
                onClick={() => setIsEdit(false)}
              />
            </div>
            <div className="flex flex-col gap-[8px] mb-3">
              <h2 className="text-[#050505] dark:text-white font-urbanist text-[32px] font-semibold leading-[125%] transition-colors">
                Edit Profile
              </h2>
              <h6 className="text-[#6A6A6A] dark:text-gray-400 font-urbanist text-[16px] font-medium leading-[140%] transition-colors">
                Enter these details for your preferences
              </h6>
            </div>

            {/* Edit Form */}
            <div className="w-full space-y-6 mt-[24px]">
              {/* First Name Field */}
              <div className="">
                <label className="text-[#050505] dark:text-white font-urbanist text-[16px] font-medium leading-[140%] mb-[12px] transition-colors">
                  First Name
                </label>
                <input
                  type="text"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full flex p-4 items-center gap-3 self-stretch rounded-[16px] border border-[#E5E5E5] dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white shadow-[0_1px_4px_0_rgba(229,229,229,0.40)] transition-colors"
                  placeholder="Enter your first name"
                />
              </div>

              {/* Last Name Field */}
              <div className="">
                <label className="text-[#050505] dark:text-white font-urbanist text-[16px] font-medium leading-[140%] mb-[12px] transition-colors">
                  Last Name
                </label>
                <input
                  type="text"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full flex p-4 items-center gap-3 self-stretch rounded-[16px] border border-[#E5E5E5] dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white shadow-[0_1px_4px_0_rgba(229,229,229,0.40)] transition-colors"
                  placeholder="Enter your last name"
                />
              </div>

              {/* Email Field */}
              <div className="">
                <label className="text-[#050505] dark:text-white font-urbanist text-[16px] font-medium leading-[140%] mb-[12px] transition-colors">
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full flex p-4 items-center gap-3 self-stretch rounded-[16px] border border-[#E5E5E5] dark:border-gray-600 bg-white dark:bg-gray-700 dark:text-white shadow-[0_1px_4px_0_rgba(229,229,229,0.40)] transition-colors"
                  placeholder="Enter your email address"
                />
              </div>

              {/* Profile Image Upload */}
              <div className="flex flex-col items-center gap-3">
                {imageUrl ? (
                  <img
                    src={imageUrl}
                    alt="Profile"
                    className="h-24 w-24 rounded-full object-cover border dark:border-gray-600"
                  />
                ) : (
                  <div className="h-24 w-24 rounded-full bg-gray-200 dark:bg-gray-700" />
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={handleImageUpload}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="text-[#7402A7] dark:text-purple-400 font-urbanist text-sm font-semibold"
                >
                  {uploading ? "Uploading..." : "Upload Photo"}
                </button>
              </div>

              <div>
                <h6 className="text-[#050505] dark:text-white font-urbanist text-[16px] font-medium leading-[140%] mb-[12px] transition-colors">
                  Select your preferences
                </h6>

                <div className="flex flex-wrap gap-3">
                  {categories.map((item, index) => {
                    const isSelected = selectedCategories.includes(item);
                    return (
                      <button
                        key={index}
                        onClick={() => toggleCategory(item)}
                        className={`px-5 py-2 text-sm font-medium rounded-full border transition ${
                          isSelected
                            ? "bg-[#7604A5] dark:bg-purple-600 text-white border-[#7604A5] dark:border-purple-600"
                            : "border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                        }`}
                      >
                        {formatCategoryDisplay(item)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="w-full mt-[32px] mb-2 pb-10 md: pb-2">
            {error && (
              <p className="text-red-500 font-urbanist text-[14px] mb-[16px]">
                {error}
              </p>
            )}
            <button
              onClick={handleSaveChanges}
              disabled={isLoading || uploading}
              className="w-full flex-1 pl-[24px] pr-[12px] py-[12px] rounded-[150px] bg-[#7402A7] dark:bg-purple-600 text-white font-urbanist text-[16px] font-semibold hover:bg-[#5a0282] dark:hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;
